/**
 * How far the DraftKings native app's bottom tab bar overlaps the Pick'em iframe.
 *
 * Pick'em runs embedded in DK's app: a native WebView loads a DK page that iframes us,
 * and the WebView runs to (or past) the bottom of the screen with DK's translucent tab
 * bar painted over its last stretch. `env(safe-area-inset-bottom)` is 0 inside the
 * iframe, so it is NOT the lever; this offset is. Anything bottom-pinned — the in-play
 * sticky-footer ad banner, the pinned submit CTA, the picks scroll padding — has to be
 * lifted clear of that overlap or it is silently covered (DKPLAT-744: "anchored ad not
 * anchored until scroll", C4 contest).
 *
 * Ported from `apps/squares/src/utils/embedChrome.ts`; the detection and the resolution
 * order are the same and the two must stay in step. Squares keeps its values in CSS
 * custom properties because its pinned elements are styled in CSS; Pick'em positions
 * its footer stack from JS numbers, so the values live here as constants instead.
 *
 * Resolution order at runtime (`resolveEmbedBottomChromePx`):
 *
 *   1. `?bottomInset=NN` on the embed URL — the embedder knows the real number, and this
 *      is the contract DK have been asked to implement. Wins whenever present and sane.
 *   2. A per-platform default, when the UA says we are inside DK's native app.
 *   3. 0 — a plain browser tab is not under anybody's tab bar.
 */

/** Embed URL parameter carrying the parent app's real bottom overlap, in CSS px. */
export const EMBED_BOTTOM_CHROME_PARAM = 'bottomInset';

/**
 * Test/harness override forcing the native-embed context on. A browser-hosted harness
 * has a UA that correctly says "browser", so without this it could only ever test the
 * web case. Same dev-affordance role as `?gamMock=1`.
 */
export const DK_NATIVE_EMBED_PARAM = 'dkNativeEmbed';

/**
 * Upper bound on an accepted `?bottomInset`. Anything larger is a mistake or a tampered
 * URL rather than a tab bar, and honouring it would push the banner off the top of a phone.
 */
export const EMBED_BOTTOM_CHROME_MAX_PX = 240;

export type EmbedPlatform = 'ios' | 'android' | 'unknown';

/**
 * Per-platform overlap defaults, in CSS px, used only when the UA says DK-native and DK
 * sent no `?bottomInset`. These are the values squares settled on after fifteen deploys
 * of tuning against prod device captures (see the history in `apps/squares/src/index.css`
 * next to `--squares-embed-bottom-chrome`):
 *
 *   - iOS 36: three measurements agree on ~30px of overlap: the two device captures
 *     documented in `apps/squares/src/utils/embedChrome.ts`, and a DK-app capture from the
 *     client on 18 Sep 2026 showing ~24pt of a 50pt creative cut off behind the tab bar.
 *     36 is that 30 plus headroom.
 *
 *     The history is worth keeping because both wrong answers are easy to re-derive. It
 *     ran 36 -> 83 -> 100 -> 120, each bump chasing a creative that still looked clipped —
 *     the trap the squares file warns about, where a capture from a build with unconfirmed
 *     anchoring reads as "the offset must be bigger" when it is really "the iframe extends
 *     ~30px past the bar". Then it went to 0, when both apps were zeroed together on the
 *     theory that one constant caused both apps' iOS defects. That was the opposite error.
 *
 *     Squares now carries 36 as well, but as a separate value in its own CSS: its banner
 *     pads differently, so tune each app against its own captures, never by copying the
 *     other's number.
 *   - Android 0: DK confirmed the Android WebView ends ABOVE the tab bar, and a non-zero
 *     value there produced the "gap at the bottom" complaint (fixed 20 Aug 2026) - the
 *     same complaint iOS was then still producing for the same reason.
 *   - unknown-platform native 75: the pre-measurement compromise, kept for a UA that
 *     admits to being a WebView but not which one. Deliberately NOT zeroed with iOS:
 *     it covers other apps' in-app browsers, where nothing has been measured.
 *
 * If a creative ever does come back covered, that is the opposite failure and the signal
 * to raise the platform's number again - but raise it against a device capture, not a
 * report, and note that DK can override any of this per-session with `?bottomInset=NN`
 * without waiting for a deploy.
 */
export const EMBED_BOTTOM_CHROME_DEFAULT_PX: Record<EmbedPlatform, number> = {
  ios: 36,
  android: 0,
  unknown: 75,
};

/**
 * Whether this document is running inside DK's native app rather than a web browser.
 *
 * The two contexts are identical from inside the frame — both are iframes, neither gets a
 * safe-area inset, and viewport width says nothing — so the user agent is the only signal:
 *
 *   - Android: an in-app WebView puts `; wv` in the UA. Chrome does not.
 *   - iOS: WKWebView omits the `Safari/` token that Mobile Safari, Chrome (CriOS) and
 *     Firefox (FxiOS) all carry.
 *
 * Other apps' in-app browsers look native too and get a reserve they do not need. That is
 * a visible, harmless band, reversible by the embedder sending `?bottomInset=0`. The
 * opposite mistake puts a paid creative under DK's tab bar — the defect this exists to fix.
 */
export const isDkNativeEmbed = (
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  search: string = typeof window === 'undefined' ? '' : window.location.search
): boolean => {
  if (new URLSearchParams(search).get(DK_NATIVE_EMBED_PARAM) === '1') return true;

  if (/Android/.test(userAgent)) return /;\s*wv\b/.test(userAgent);
  if (/iPhone|iPad|iPod/.test(userAgent)) return !/Safari\//.test(userAgent);

  return false;
};

/** The platform, from the user agent. Coarse on purpose: it only picks a default row. */
export const embedPlatform = (
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent
): EmbedPlatform => {
  if (/Android/.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios';
  return 'unknown';
};

/**
 * Parse a bottom-inset override out of a query string. Null when absent or not a finite
 * number within range — a bad value must leave the default standing rather than collapse
 * the offset to 0 and clip the ad.
 */
export const readEmbedBottomChromeOverride = (search: string): number | null => {
  const raw = new URLSearchParams(search).get(EMBED_BOTTOM_CHROME_PARAM);
  if (raw === null || raw.trim() === '') return null;

  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0 || value > EMBED_BOTTOM_CHROME_MAX_PX) return null;

  return Math.round(value);
};

export type EmbedBottomChromeSource = 'applied' | 'default' | 'browser';

export interface EmbedBottomChromeResolution {
  /** The px every bottom-pinned element should clear. */
  px: number;
  /** Where the number came from — for logging and telemetry, not layout. */
  source: EmbedBottomChromeSource;
  embedContext: 'dk-native' | 'browser';
  embedPlatform: EmbedPlatform;
}

/** Resolve the overlap for this page load. Pure; every input defaults to the live document. */
export const resolveEmbedBottomChrome = (
  search: string = typeof window === 'undefined' ? '' : window.location.search,
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent
): EmbedBottomChromeResolution => {
  const native = isDkNativeEmbed(userAgent, search);
  const platform = embedPlatform(userAgent);
  const override = readEmbedBottomChromeOverride(search);

  if (override !== null) {
    return {
      px: override,
      source: 'applied',
      embedContext: native ? 'dk-native' : 'browser',
      embedPlatform: platform,
    };
  }
  if (native) {
    return {
      px: EMBED_BOTTOM_CHROME_DEFAULT_PX[platform],
      source: 'default',
      embedContext: 'dk-native',
      embedPlatform: platform,
    };
  }
  return { px: 0, source: 'browser', embedContext: 'browser', embedPlatform: platform };
};

/** Convenience: just the number. */
export const resolveEmbedBottomChromePx = (search?: string, userAgent?: string): number =>
  resolveEmbedBottomChrome(search, userAgent).px;
